import { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useTranslation } from 'react-i18next'
import type { ResolvedCredential, ResolvedRedemption, ResolvedText } from '@/services/myBenefits'
import { RedemptionCredentialKinds } from '@/services/myBenefits'
import { redemptionText } from '../redemptionText'

/**
 * What to do with a benefit that has been claimed — **as the benefit itself says**.
 *
 * This replaced three sentences hard-coded on the benefits page and again in the mobile app,
 * identical in both and true only of a club front desk. Nothing about a front desk survives: the
 * title, the steps, the code and the link all come off the claim, so an online booking link or a
 * scannable voucher is an admin change rather than two client releases.
 *
 * ## The layout follows what the member has to do, in order
 *
 * Read the steps, get the things you need (an identity to read out, a code to be scanned), take the
 * action (a link), then the small print. A benefit is either shown at a counter or opened in a
 * browser, never both, so the block stays short whichever kind it is.
 *
 * ## Unknown kinds render, they do not break
 *
 * Nothing here switches on `kind`. Every method has a title, steps and a footnote, and the extras
 * are drawn from the presence of their own fields — so a deploy that meets a fourth method it has
 * never heard of still shows the instructions instead of a blank card.
 *
 * ## Collapsible, because a QR is a large thing to unfurl
 *
 * The title row is the toggle. A redeemed or expired claim starts closed — its instructions are
 * still worth having, but a code for a claim settled last month pushes every other benefit down the
 * page. Everything live starts open, because that is what the member came for.
 *
 * ## Nothing to say draws nothing, and that is the caller's whole conditional
 *
 * A benefit that is unclaimed or waiting on a coach resolves to the server's null object: no steps.
 * Handled HERE rather than by every caller, so a card renders this unconditionally and cannot
 * forget the case. An empty white box is the bug this prevents.
 */
export const RedemptionSteps = ({
  redemption,
  worth,
  initiallyExpanded = true,
}: {
  redemption: ResolvedRedemption
  /**
   * What the benefit is worth, already formatted by this app, for the identity block to carry.
   *
   * Composed by the caller rather than sent as a field: every other row is a plain string the
   * server hands over unchanged, but an amount has a currency symbol and a plural, and formatting
   * it server-side would freeze the server's locale into a row a member reads.
   */
  worth?: string | null
  /**
   * Whether the block starts open.
   *
   * Open while there is something to do with the benefit, closed once there is not — see
   * `startsExpanded`. Only the STARTING state: a member can always open a finished claim to see
   * what they were told, and close a live one whose QR is taking up the page.
   */
  initiallyExpanded?: boolean
}) => {
  const { t, i18n } = useTranslation()
  const [expanded, setExpanded] = useState(initiallyExpanded)
  const line = (text: ResolvedText | null | undefined) => redemptionText(i18n, text)

  const footnote = line(redemption.footnote)

  // The null object, and anything else with nothing in it. Not a `kind` check: a method with no
  // steps and no link has nothing to draw whatever it calls itself, and that is the property worth
  // testing. A link alone is enough — a partner may author nothing but where to go.
  if (redemption.steps.length === 0 && !redemption.url) return null

  return (
    <div className="mt-3 rounded-md bg-white px-3 py-2.5 ring-1 ring-gray-200">
      <button
        type="button"
        className="flex w-full items-center justify-between text-left"
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
      >
        <span className="text-xs font-medium text-gray-900">{line(redemption.title)}</span>
        <span aria-hidden className="ml-2 text-[10px] text-gray-400">
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {expanded && (
        <>
          {/* An ordered list rather than numbered strings: the order is the meaning, and a screen
          reader announces "list, 3 items, item 1" instead of reading a literal "1." */}
          <ol className="mt-1.5 list-inside list-decimal space-y-1 text-xs text-gray-600">
            {redemption.steps.map((step, index) => (
              // The server owns the order and the wording; there is no id, and two steps may
              // legitimately read the same. Position IS the identity of a step in an ordered list.
              <li key={`${index}-${step.i18nKey ?? step.text}`}>{line(step)}</li>
            ))}
          </ol>

          {redemption.credentials.map((credential) => (
            <Credential
              key={credential.kind}
              credential={credential}
              label={line(credential.label)}
              renderText={line}
              worth={worth ?? null}
            />
          ))}

          {!!redemption.url && (
            <a
              href={redemption.url}
              // An authored link points off DUPR entirely, so it opens away from the page a member is
              // reading and cannot reach back into it through `window.opener`.
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block rounded-full bg-[#3A79E0] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2D5FB8]"
            >
              {/* A link with no authored label still needs words on the button. */}
              {line(redemption.linkLabel) || t('benefits.redemptionOnlineLinkLabel')}
            </a>
          )}

          {!!footnote && <p className="mt-2.5 text-xs text-gray-400">{footnote}</p>}
        </>
      )}
    </div>
  )
}

/**
 * What the member holds up at the counter.
 *
 * Two shapes today — an identity a person reads and writes down, and a code a scanner reads — and a
 * club rolling out a scanner shows both at once. An unrecognized kind draws nothing rather than
 * guessing: a wrong block at a counter costs a member a wasted trip, whereas a missing one still
 * leaves the steps above telling them where to go.
 */
const Credential = ({
  credential,
  label,
  renderText,
  worth,
}: {
  credential: ResolvedCredential
  label: string
  renderText: (text: ResolvedText | null | undefined) => string
  worth: string | null
}) => {
  const { t } = useTranslation()

  if (credential.kind === RedemptionCredentialKinds.ScanCode) {
    return credential.payload ? <ScanCode payload={credential.payload} prompt={label} /> : null
  }

  if (credential.kind !== RedemptionCredentialKinds.Identity) return null

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <dl className="mt-1.5 space-y-1">
        {(credential.fields ?? []).map((field) => (
          <Row key={field.label.i18nKey ?? field.label.text} label={renderText(field.label)} value={field.value} />
        ))}
        {!!worth && <Row label={t('benefits.worthLabel')} value={worth} />}
      </dl>
    </div>
  )
}

/**
 * One line a member reads out and staff write down.
 *
 * `select-all` and tabular figures because a claim reference is read a character at a time across a
 * counter; the server's alphabet already drops the worst confusable pairs, this makes the rest
 * legible.
 */
const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-baseline justify-between gap-3">
    <dt className="shrink-0 text-xs text-gray-500">{label}</dt>
    <dd className="select-all text-right text-xs font-medium tabular-nums text-gray-900">{value}</dd>
  </div>
)

/**
 * The member's half of a scan redemption: a code, and a person to show it to.
 *
 * ## Why the plate is hard-coded white
 *
 * A QR reader needs a dark-on-light image with a quiet zone around it. A code drawn in the page's
 * greys, or inheriting a card that is not white, is one that phones fail to read at an angle, in a
 * badly lit lobby, held by somebody in a hurry. The colors are a physical constraint rather than a
 * styling choice, so they are stated here instead of tokenised.
 */
const ScanCode = ({ payload, prompt }: { payload: string; prompt: string }) => (
  <div className="mt-3 flex flex-col items-center">
    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{prompt}</p>
    <div className="mt-2 rounded-md border border-gray-200 bg-white p-3">
      <QRCodeSVG
        value={payload}
        size={168}
        bgColor="#FFFFFF"
        fgColor="#000000"
        // The quiet zone the spec asks for, in modules. Without it a code that touches its own
        // border is measurably harder for a reader to lock onto.
        marginSize={2}
        // A QR is opaque to a screen reader, so it announces its PURPOSE. Reading the payload out
        // would be worse than silence: it is a URI nobody can act on by ear.
        title={prompt}
      />
    </div>
  </div>
)

export default RedemptionSteps
